import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {BehaviorSubject, Observable, Subject, combineLatest, of} from 'rxjs';
import {finalize, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Comment as ApiComment} from '../../api/models/comment';
import {CommentBundle} from '../../api/models/comment-bundle';
import {CommentParentType} from '../../api/models/comment-parent-type';
import {Invoice} from '../../api/models/invoice';
import {CommentsService} from '../../api/services/comments.service';
import {InvoicePeerService} from '../../service/invoice-peer.service';
import {RealtimeNotificationsService} from '../../service/realtime-notifications.service';

type TabIndex = 0 | 1 | 2 | 3;

interface WidgetData {
  // Four tab lists, index-aligned with the tab group: priority / client / end / invoice.
  tabs: ApiComment[][];
}

@Component({
  selector: 'app-invoice-comments-v2-widget',
  templateUrl: './invoice-comments-v2-widget.component.html',
  styleUrls: ['./invoice-comments-v2-widget.component.scss']
})
export class InvoiceCommentsV2WidgetComponent implements OnInit, OnDestroy {
  private invoicePeerService = inject(InvoicePeerService);
  private commentsService = inject(CommentsService);
  private realtime = inject(RealtimeNotificationsService);
  private builder = inject(UntypedFormBuilder);

  // Card visibility is gated on invoice$ (emits as soon as the modern peer
  // resolves) rather than on data$ — otherwise the card would stay hidden
  // for an extra round-trip while the comment bundle loads, making the
  // widget appear systematically later than its v2 siblings.
  public invoice$: Observable<Invoice | null>;
  public data$: Observable<WidgetData>;
  public invoice: Invoice | null = null;
  public selectedItem: ApiComment | null = null;
  public selectedIndex: TabIndex = 0;

  public form: UntypedFormGroup;
  public locked = false;
  public added = false;
  public edited = false;
  public saving = false;

  private readonly reload$ = new BehaviorSubject<void>(undefined);
  private readonly ngDestroy$ = new Subject<void>();

  constructor() {
    this.form = this.builder.group({
      body: [null, Validators.required],
      priority: [false]
    });
  }

  public ngOnInit(): void {
    this.invoice$ = this.invoicePeerService.getInvoice();

    this.data$ = combineLatest([this.invoice$, this.reload$]).pipe(
      tap(([invoice]) => (this.invoice = invoice)),
      switchMap(([invoice]) => {
        if (!invoice) {
          return of(this.emptyData());
        }
        return this.commentsService.getInvoiceCommentBundle({id: invoice.id}).pipe(
          map((bundle) => this.bundleToTabs(bundle))
        );
      }),
      startWith(this.emptyData())
    );

    this.invoicePeerService
      .getState()
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((state) => {
        this.locked = state === 'locked';
      });

    // Reload the bundle whenever another client or background process mutates
    // a comment attached to the current invoice, its client, or its end
    // customer. The server-side fan-out already filters to relevant page
    // instances; the match check below defends against payloads arriving
    // during an in-flight invoice switch.
    this.realtime.events$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((event) => {
        if (event.event_type !== 'comment_changed' || !this.invoice) {
          return;
        }
        const matchesInvoice = event.parent_type === CommentParentType.Invoice && event.parent_id === this.invoice.id;
        const matchesClient = event.parent_type === CommentParentType.Client && event.parent_id === this.invoice.client_id;
        const matchesEndCustomer =
          event.parent_type === CommentParentType.EndCustomer && event.parent_id === this.invoice.end_customer_id;
        if (matchesInvoice || matchesClient || matchesEndCustomer) {
          this.reload$.next();
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  public tabIndexChanged(index: number): void {
    this.selectedIndex = index as TabIndex;
    this.selectedItem = null;
  }

  public itemClicked(item: ApiComment): void {
    this.selectedItem = item !== this.selectedItem ? item : null;
  }

  public isAddable(): boolean {
    if (this.added || this.edited) {
      return false;
    }
    switch (this.selectedIndex) {
      case 0: // Priority tab is read-only — comments are added via their parent tab.
        return false;
      case 2: // End customer tab requires the invoice to have an end customer.
        return this.invoice?.end_customer_id != null;
      default:
        return true;
    }
  }

  public add(): void {
    if (!this.isAddable()) {
      return;
    }
    this.invoicePeerService.lock();
    this.added = true;
    this.selectedItem = null;
    this.form.reset({body: null, priority: false});
  }

  public edit(): void {
    if (!this.selectedItem) {
      return;
    }
    this.invoicePeerService.lock();
    this.edited = true;
    this.form.patchValue({
      body: this.selectedItem.body,
      priority: this.selectedItem.priority
    });
  }

  public save(): void {
    if (!this.form.valid || this.saving || !this.invoice) {
      return;
    }
    this.saving = true;
    const body = this.form.value.body as string;
    const priority = this.form.value.priority === true;

    const request$ = this.added ? this.createFor(this.selectedIndex, body, priority) : this.updateSelected(body, priority);
    // finalize: HttpErrorInterceptor completes a 401, a 403 or an aborted
    // request without a value or an error, which would leave saving set.
    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: () => this.finishSave(),
      error: () => (this.saving = false)
    });
  }

  public remove(): void {
    if (!this.selectedItem || this.saving) {
      return;
    }
    this.saving = true;
    this.commentsService.deleteComment({id: this.selectedItem.id}).pipe(finalize(() => (this.saving = false))).subscribe({
      next: () => this.finishSave(),
      error: () => (this.saving = false)
    });
  }

  public revert(): void {
    this.invoicePeerService.unlock();
    this.added = false;
    this.edited = false;
    if (this.selectedItem && this.selectedItem.id === 0) {
      this.selectedItem = null;
    }
    this.form.reset({body: null, priority: false});
  }

  private createFor(index: TabIndex, body: string, priority: boolean): Observable<ApiComment> {
    if (!this.invoice) {
      return of({} as ApiComment);
    }
    const payload = {body, priority};
    switch (index) {
      case 1:
        return this.commentsService.createClientComment({id: this.invoice.client_id, body: payload});
      case 2:
        return this.commentsService.createEndCustomerComment({id: this.invoice.end_customer_id!, body: payload});
      case 3:
        return this.commentsService.createInvoiceComment({id: this.invoice.id, body: payload});
      default:
        throw new Error('cannot create in priority tab');
    }
  }

  private updateSelected(body: string, priority: boolean): Observable<ApiComment> {
    if (!this.selectedItem) {
      return of({} as ApiComment);
    }
    return this.commentsService.updateComment({
      id: this.selectedItem.id,
      body: {body, priority}
    });
  }

  private finishSave(): void {
    this.invoicePeerService.unlock();
    this.selectedItem = null;
    this.saving = false;
    this.added = false;
    this.edited = false;
    this.form.reset({body: null, priority: false});
    this.reload$.next();
  }

  private bundleToTabs(bundle: CommentBundle): WidgetData {
    const priority = [
      ...bundle.client.filter((c) => c.priority),
      ...bundle.end_customer.filter((c) => c.priority),
      ...bundle.invoice.filter((c) => c.priority)
    ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return {
      tabs: [priority, bundle.client, bundle.end_customer, bundle.invoice]
    };
  }

  private emptyData(): WidgetData {
    return {tabs: [[], [], [], []]};
  }
}
